import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { DateTime } from 'luxon';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import { cleanSorters } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { reloadCrmTradersCatalog, selectCrmTradersListRows } from '../../utils/crmCatalogStore';
import { restoreCrmSetupLocalListMask } from '../../utils/crmSetupListMask';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, ExportIcon, RefreshIcon, VerticalLineIcon, EditRowIcon, FavoriteIcon, PriceIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, SearchIcon } from '../svg/svgIcons';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';

const CUSTOMER_TRADERS_LIST_COLUMN_I18N_KEYS = Object.freeze({
  id: 'ServiceTaskTable.Col.Id',
  employee_name: 'CustomerTradersList.Col.Name',
  username: 'CustomerTradersList.Col.Username',
  supervisors_text: 'CustomerTradersList.Col.Supervisors',
  active: 'ServiceTaskTable.Col.State',
  started_at: 'CustomerTradersList.Col.StartDate',
  ended_at: 'CustomerTradersList.Col.EndDate',
  created_at: 'CustomerStatesList.Col.Created',
  updated_at: 'ServiceTaskTable.Col.Updated',
});

const DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  employee_name: true,
  username: true,
  supervisors_text: true,
  active: true,
  started_at: true,
  ended_at: true,
  created_at: true,
  updated_at: true,
});

const PAGE_LIST_KEY = 'CustomerTradersList';
const PAGE_ADD_KEY = 'CustomerTradersAdd';
const MASK_ADD = 'cust010';
function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE };
  return merged;
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) table.hideColumn?.(f);
    else table.showColumn?.(f);
  }
  setTimeout(() => table.redraw?.(true), 0);
}

export default function CustomerTradersList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { toast, openFavoriteDialog, setFastView, setUserMaskFilter, setStandardMaskFilter, openChosenNotificationPreferences, getDateFormat } = useContext(UtilContext);
  const tableRef = useRef(null);
  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const getTable = useCallback(() => tableRef.current?.table ?? tableRef.current, []);

  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: CUSTOMER_TRADERS_LIST_COLUMN_I18N_KEYS,
      headerFilters: {
        active: {
          placeholder: t('ServiceTaskTable.Col.State'),
          options: [
            { label: t('Common.Active'), value: 'true' },
            { label: t('Common.Inactive'), value: 'false' },
          ],
        },
      },
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const catalogRows = useMemo(() => selectCrmTradersListRows(state), [state.crm_traders]);
  const reloadCatalog = useCallback(() => reloadCrmTradersCatalog(dispatch, getRequest), [dispatch]);
  const canAdd = !!user?.permissions?.includes('add customer_management-setup');
  const canEdit = !!user?.permissions?.includes('edit customer_management-setup');
  const canDelete = !!user?.permissions?.includes('delete customer_management-setup');
  const canSetStandardMask = !!user?.permissions?.includes('set standard mask');
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'CustomerTradersList');
    if (!row) return false;
    const attributes = typeof row.attributes === 'string' ? (() => { try { return JSON.parse(row.attributes); } catch { return []; } })() : (row.attributes || []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const savedCols = typeof row.columns === 'string' ? (() => { try { return JSON.parse(row.columns); } catch { return {}; } })() : (row.columns || {});
    const merged = mergeVisibility(savedCols && typeof savedCols === 'object' ? savedCols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const normalizeText = useCallback((value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(), []);
  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    const needle = normalizeText(headerValue);
    if (!needle) return true;
    return normalizeText(rowValue).includes(needle);
  }, [normalizeText]);
  const dateCellText = useCallback((value) => {
    if (!value) return '';
    const dt = DateTime.fromISO(String(value));
    if (!dt.isValid) return String(value);
    return dt.toFormat(dateTimeFmt);
  }, [dateTimeFmt]);
  const dateOrUserFilter = useCallback((headerValue, _rowValue, rowData, filterParams) => {
    if (!headerValue || String(headerValue).trim() === '') return true;
    const raw = String(headerValue).trim();
    if (raw.startsWith('range:')) {
      const [, fromRaw = '', toRaw = ''] = raw.match(/^range:([^:]+):([^:]+)$/) || [];
      if (!fromRaw || !toRaw) return true;
      const from = DateTime.fromFormat(fromRaw, 'yyyy-MM-dd').startOf('day');
      const to = DateTime.fromFormat(toRaw, 'yyyy-MM-dd').endOf('day');
      if (!from.isValid || !to.isValid) return true;
      const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
      if (!dt.isValid) return false;
      return dt >= from && dt <= to;
    }
    const needle = normalizeText(raw);
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    if (!dt.isValid) return false;
    return normalizeText(`${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}`).includes(needle);
  }, [dateFmt, dateTimeFmt, normalizeText]);

  const getSelectedRows = useCallback(() => {
    const table = getTable();
    return table?.getSelectedData?.() ?? [];
  }, [getTable]);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    setEditIcon(!(canEdit && count === 1));
    setCopyIcon(!(canAdd && count === 1));
    setDeleteIcon(!(canDelete && count > 0));
  }, [canAdd, canDelete, canEdit]);

  function openAdd(type, row = null) {
    const translationKey = type === 'add' ? 'CustomerTradersAdd.New' : 'CustomerTradersAdd.Title';
    const legacyLabel = t(translationKey);
    dispatch({
      type: 'create_new_tab',
      name: legacyLabel,
      translationKey,
      legacyLabel,
      page: PAGE_ADD_KEY,
      props: { crm_trader: row, type },
      // non-progressive pattern: list keeps list mask, detail/add keeps add mask
      mask: MASK_ADD,
    });
  }

  const deleteSelected = useCallback(async () => {
    const selected = getSelectedRows();
    const ids = selected
      .map((row) => Number(row?.id || 0))
      .filter((id) => id > 0);
    if (!ids.length) return;
    try {
      const results = await Promise.all(ids.map((id) => deleteRequest(`/api/crm_traders/${id}`)));
      const failed = results.find((resp) => {
        const status = Number(resp?.status ?? 0);
        return status < 200 || status >= 300;
      });
      if (failed) {
        throw new Error(t('ServiceTask.DeleteFailed'));
      }
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerTradersList.Toast.Deleted'), life: 2500 });
    } catch (e) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.Warning'), detail: e?.message || t('ServiceTask.DeleteFailed'), life: 4000 });
    } finally {
      reloadCatalog();
    }
  }, [getSelectedRows, reloadCatalog, t, toast]);

  function confirmDelete() {
    if (!canDelete) return;
    const selected = getSelectedRows();
    if (!selected.length) return;
    confirmDialog({
      message: t('CustomerTradersList.DeleteConfirmMessage'),
      header: t('Common.ConfirmDelete'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      acceptClassName: 'p-button-danger',
      accept: deleteSelected,
    });
  }

  function onEditSelected() {
    if (!canEdit) return;
    const selected = getSelectedRows();
    if (!selected.length) return;
    openAdd('edit', selected[0]);
  }

  function onCopySelected() {
    if (!canAdd) return;
    const selected = getSelectedRows();
    if (!selected.length) return;
    const row = selected[0];
    openAdd('add', { ...row, id: null });
  }

  const applyToolbarSearch = useCallback((value) => {
    const table = tableRef.current?.table ?? tableRef.current;
    if (!table) return;
    const term = String(value || '').trim().toLowerCase();
    if (!term) {
      table.clearFilter(true);
      return;
    }
    table.setFilter((data) => {
      const hay = `${data?.id ?? ''} ${data?.employee_name ?? ''} ${data?.username ?? ''} ${data?.supervisors_text ?? ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, []);

  const searchTable = useCallback(() => {
    applyToolbarSearch(searchValue);
    setSearchIconColor(String(searchValue || '').trim() ? 'active' : '');
    setVisibleSearch(false);
  }, [applyToolbarSearch, searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setSearchIconColor('');
    applyToolbarSearch('');
  }, [applyToolbarSearch]);

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openAdd('edit', rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  const columns = useMemo(() => ([
    {
      field: 'rowSelection',
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      resizable: false,
      frozen: true,
      print: false,
      download: false,
    },
    {
      field: '_edit',
      formatter: reactFormatter(<EditRowFormatter />),
      width: 55,
      hozAlign: 'center',
      headerSort: false,
      frozen: true,
      resizable: false,
      title: '',
      print: false,
      download: false,
    },
    { title: t('ServiceTaskTable.Col.Id'), field: 'id', sorter: 'number', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.6, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    { title: t('CustomerTradersList.Col.Name'), field: 'employee_name', sorter: 'string', minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerTradersList.Col.Name')), widthGrow: 1, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    { title: t('CustomerTradersList.Col.Username'), field: 'username', sorter: 'string', minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerTradersList.Col.Username')), widthGrow: 1, hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
    {
      title: t('CustomerTradersList.Col.Supervisors'),
      field: 'supervisors_text',
      sorter: 'string',
      minWidth: tabulatorColumnMinWidthFromTitle(t('CustomerTradersList.Col.Supervisors')),
      widthGrow: 1.1,
      hozAlign: 'left',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textHeaderFilter,
    },
    {
      title: t('ServiceTaskTable.Col.State'),
      field: 'active',
      sorter: 'boolean',
      formatter: (cell) => (cell.getValue() ? t('Common.Active') : t('Common.Inactive')),
      headerFilter: primeMultiSelectHeaderFilter,
      headerFilterParams: {
        placeholder: t('ServiceTaskTable.Col.State'),
        valueType: 'string',
        options: [
          { label: t('Common.Active'), value: 'true' },
          { label: t('Common.Inactive'), value: 'false' },
        ],
      },
      headerFilterFunc: (headerValue, _rowValue, rowData) => {
        if (!headerValue || String(headerValue).trim() === '') return true;
        const selected = String(headerValue).split(',').map((s) => s.trim()).filter(Boolean);
        if (!selected.length) return true;
        return selected.includes(String(!!rowData?.active));
      },
      minWidth: tabulatorColumnMinWidthFromTitle(t('ServiceTaskTable.Col.State'), 'multiselect'),
      widthGrow: 0.8,
      hozAlign: 'left',
    },
    {
      title: t('CustomerTradersList.Col.StartDate'),
      field: 'started_at',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      formatter: (cell) => dateCellText(cell.getValue()),
      minWidth: tabulatorDateUserColumnMinWidth(t('CustomerTradersList.Col.StartDate')),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateOrUserFilter,
      headerFilterFuncParams: { name: 'started_at' },
    },
    {
      title: t('CustomerTradersList.Col.EndDate'),
      field: 'ended_at',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      formatter: (cell) => dateCellText(cell.getValue()),
      minWidth: tabulatorDateUserColumnMinWidth(t('CustomerTradersList.Col.EndDate')),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateOrUserFilter,
      headerFilterFuncParams: { name: 'ended_at' },
    },
    {
      title: t('CustomerStatesList.Col.Created'),
      field: 'created_at',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      formatter: (cell) => dateCellText(cell.getValue()),
      minWidth: tabulatorDateUserColumnMinWidth(t('CustomerStatesList.Col.Created')),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateOrUserFilter,
      headerFilterFuncParams: { name: 'created_at' },
    },
    {
      title: t('ServiceTaskTable.Col.Updated'),
      field: 'updated_at',
      sorter: 'datetime',
      sorterParams: { format: 'iso' },
      formatter: (cell) => dateCellText(cell.getValue()),
      minWidth: tabulatorDateUserColumnMinWidth(t('ServiceTaskTable.Col.Updated')),
      widthGrow: 1,
      hozAlign: 'left',
      headerFilter: dateUserHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
      headerFilterFunc: dateOrUserFilter,
      headerFilterFuncParams: { name: 'updated_at' },
    },
  ]), [dateCellText, dateFmt, dateOrUserFilter, dateTimeFmt, t, textHeaderFilter]);

  const closeMenu = () => {
    setExpandedKeys({});
  };

  const setMask = (field, value) => {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE, ...columnsVisible, [field]: value };
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  };

  const maskSelectAll = () => {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  };

  const maskDeselectAll = () => {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  };

  const saveUserMaskColumns = () => {
    const table = getTable();
    const filtersSnapshot = table?.getHeaderFilters?.() ?? [];
    const attributes = table?.getColumnLayout?.() ?? null;
    setUserMaskFilter('CustomerTradersList', 'all', columnsVisible, filtersSnapshot, attributes, null);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('Common.SaveStandardMaskLayoutPrompt'),
      header: t('Common.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = tableRef.current?.table ?? tableRef.current;
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        const colsForSave = mergeVisibility(layout.columns || {});
        setStandardMaskFilter('CustomerTradersList', colsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'CustomerTradersList');
    if (!standard) return;
    const attributes = typeof standard.attributes === 'string' ? (() => { try { return JSON.parse(standard.attributes); } catch { return []; } })() : (standard.attributes || []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const savedCols = typeof standard.columns === 'string' ? (() => { try { return JSON.parse(standard.columns); } catch { return {}; } })() : (standard.columns || {});
    const colsForSave = mergeVisibility(savedCols && typeof savedCols === 'object' ? savedCols : {});
    setUserMaskFilter('CustomerTradersList', 'all', colsForSave, null, attributes, null, () => {
      const table = tableRef.current?.table ?? tableRef.current;
      if (!table) return;
      try { table.setColumnLayout?.(attributes); } catch (_) {}
      setColumnsVisible(colsForSave);
      applyDataColumnVisibilityToTable(table, colsForSave);
    });
  };

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('ServiceTask.SetFilter'), icon: 'pi pi-filter', command: () => { const table = getTable(); setUserMaskFilter('CustomerTradersList', 'set_filters', null, table?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('ServiceTask.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { const table = getTable(); setUserMaskFilter('CustomerTradersList', 'set_filters', null, [], null); table?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      {
        key: '0_3',
        label: t('ServiceTask.SetSort'),
        icon: 'pi pi-sort-alt',
        command: () => {
          const table = getTable();
          setUserMaskFilter('CustomerTradersList', 'set_sorters', null, null, null, cleanSorters(table?.getSorters?.() ?? []));
          closeMenu();
        },
        template: itemRenderer,
      },
      {
        key: '0_4',
        label: t('Common.ClearSorting'),
        icon: 'pi pi-sort-alt-slash',
        command: () => {
          const table = getTable();
          setUserMaskFilter('CustomerTradersList', 'set_sorters', null, null, null, []);
          table?.setSort?.([]);
          closeMenu();
        },
        template: itemRenderer,
      },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); closeMenu(); }, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_6', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences?.('CustomerTradersList'); closeMenu(); }, template: itemRenderer },
    ],
  }];

  const printRows = () => {
    const table = getTable();
    if (!table) return;
    const filters = table.getHeaderFilters?.() ?? [];
    const params = {};
    filters.forEach((f) => {
      const field = f?.field;
      const value = f?.value;
      if (!field || value == null || String(value).trim() === '') return;
      params[`filter_${field}`] = String(value);
    });
    const sorters = cleanSorters(table.getSorters?.() ?? []);
    const printColumns = (table.getColumns?.() ?? [])
      .map((col) => col?.getField?.() ?? col?.getDefinition?.()?.field)
      .filter((field) => Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field) && columnsVisible[field] !== false);
    const suffix = buildExportQueryStringFromParams({
      ...params,
      toolbar_search: String(searchValue ?? '').trim() || undefined,
      sorters: JSON.stringify(sorters),
    }, {
      includePrintColumns: true,
      printLayout: printLayoutChoice,
      printOrientation: printOrientationChoice,
      printColumns,
    });
    setPrintChoiceVisible(false);
    fetchTextGet(`/api/crm_traders/export/print${suffix}`)
      .then((html) => {
        if (!openPrintHtmlWindow(html)) {
          toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('Common.EnablePopups'), life: 3500 });
        }
      })
      .catch((e) => {
        toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4000 });
      });
  };

  const exportRows = async () => {
    const table = getTable();
    if (!table) return;
    const filters = table.getHeaderFilters?.() ?? [];
    const params = {};
    filters.forEach((f) => {
      const field = f?.field;
      const value = f?.value;
      if (!field || value == null || String(value).trim() === '') return;
      params[`filter_${field}`] = String(value);
    });
    const sorters = cleanSorters(table.getSorters?.() ?? []);
    const printColumns = (table.getColumns?.() ?? [])
      .map((col) => col?.getField?.() ?? col?.getDefinition?.()?.field)
      .filter((field) => Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, field) && columnsVisible[field] !== false);
    const suffix = buildExportQueryStringFromParams({
      ...params,
      toolbar_search: String(searchValue ?? '').trim() || undefined,
      sorters: JSON.stringify(sorters),
    }, { includePrintColumns: true, printColumns });
    const docTitle = t(EXPORT_DOCUMENT_TITLE_KEYS.CRM_TRADERS);
    const defaultXlsx = xlsxDownloadFilename(docTitle);
    try {
      const { blob, filename } = await downloadBlobGet(`/api/crm_traders/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4000 });
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button title={t('Icon.AddNew')} className="disabled:opacity-25" disabled={!canAdd} onClick={() => openAdd('add')} type="button"><AddIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button title={t('Icon.Edit')} className="disabled:opacity-25" onClick={onEditSelected} disabled={editIcon} type="button"><EditIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button title={t('Icon.Copy')} className="disabled:opacity-25" onClick={onCopySelected} disabled={copyIcon} type="button"><CopyIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button title={t('Icon.Delete')} className="disabled:opacity-25" onClick={confirmDelete} disabled={deleteIcon} type="button"><DeleteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
          <button title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor === 'active' ? clearSearch : () => setVisibleSearch(true)}>
            {searchIconColor === '' ? (
              <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
            ) : (
              <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
            )}
          </button>
        </div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button title={t('Common.ExportToExcel')} onClick={exportRows}><ExportIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh"><button title={t('Common.Refresh')} onClick={reloadCatalog}><RefreshIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.First')} onClick={() => { const table = getTable(); table?.scrollToRow?.(catalogRows?.[0]?.id, 'top', false); }}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.Previous')} onClick={() => { const table = getTable(); table?.navigatePrev?.(); }}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.Next')} onClick={() => { const table = getTable(); table?.navigateNext?.(); }}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button title={t('Icon.Last')} onClick={() => { const table = getTable(); const last = catalogRows?.[catalogRows.length - 1]; if (last) table?.scrollToRow?.(last.id, 'bottom', false); }}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button title={t('Common.AddToFavorites')} onClick={() => openFavoriteDialog?.(t('CustomerTradersAdd.FavoriteTitle'), PAGE_LIST_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="price"><button title={t('Common.QuickView')} onClick={() => setFastView?.(PAGE_LIST_KEY)}><PriceIcon /></button></div>
        {canSetStandardMask ? (
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
            <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
              <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
            </button>
          </div>
        ) : null}
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]">
          <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
        </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                onRef={(r) => { tableRef.current = r?.current; }}
                data={catalogRows}
                columns={columns}
                className="h-[calc(100vh_-_193px)]"
                selectable
                selectableRows={true}
                placeholder={t('CustomerTradersList.NoRows')}
                options={{
                  movableColumns: true,
                  resizableRows: false,
                  pagination: true,
                  paginationSize: 50,
                  layout: 'fitColumns',
                  headerSortTristate: true,
                  filterMode: 'local',
                  sortMode: 'local',
                }}
                events={{
                  rowSelectionChanged: onSelectRow,
                  renderComplete: () => {
                    const selected = getTable()?.getSelectedData?.() ?? [];
                    if (selected.length === 0) {
                      setEditIcon(true);
                      setCopyIcon(true);
                      setDeleteIcon(true);
                    }
                  },
                  tableBuilt: () => {
                    const table = getTable();
                    restoreCrmSetupLocalListMask({
                      pageKey: PAGE_LIST_KEY,
                      state,
                      table,
                      mergeVisibility,
                      applyDataColumnVisibilityToTable,
                      setColumnsVisible,
                      defaultColumnFields: Object.keys(DEFAULT_COLUMNS_VISIBLE),
                    });
                    const selected = table?.getSelectedData?.() ?? [];
                    onSelectRow(selected);
                  },
                  ...getTabulatorPersistLayoutEvents(PAGE_LIST_KEY, tableRef, setUserMaskFilter),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex items-center gap-3 w-full">
          <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
          <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"><SearchIcon /></button>
        </div>
      </Dialog>
      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={printRows}
      />
      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('Common.SetMask')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('Common.SelectAll')}
        deselectAllLabel={t('Common.DeselectAll')}
        saveLabel={t('Common.Save')}
        getFieldLabel={(field) => {
          const col = columns.find((item) => item?.field === field);
          return col?.title || field;
        }}
        inputIdPrefix="crm-trader-mask"
      />
    </div>
  );
}

